

















































































































import { Component, Prop, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import BrandsStore from "@/mixins/brandsStore";

// Enums
import { ProductTypes, RelatedProductsTypes } from "@/data/enums/products";
import { DataModules } from "@/store/modules/data";
import { ConfirmModalModes } from "@/data/enums";

// Types
import {
  IProduct,
  IProductCategory,
  ISelectedProduct,
  IRelatedObject
} from "@/models/products";
import { IState } from "@/store";

@Component({
  computed: {
    ...mapState({
      rootCategories(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: this.categoriesModule,
          scope: this.rootCategoriesScope
        });
      },
      rootCategoriesScope(state: IState, getters) {
        return getters[`data/${this.categoriesModule}/rootCategoriesScope`](
          this.brandId,
          [ProductTypes.SINGLE_PRODUCT]
        );
      },

      productWithRelatedScope(state: IState, getters) {
        return getters[`data/${this.dataModule}/withRelatedScope`](
          this.productId
        );
      },

      product(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: this.dataModule,
          scope: this.productWithRelatedScope
        });
      }
    })
  }
})
export default class CrossSellingProducts extends Mixins(
  RequestPromises,
  BrandsStore
) {
  // Getters
  public rootCategoriesScope!: string;
  public rootCategories!: IProductCategory[];
  public productWithRelatedScope!: string;
  public product!: IProduct;

  @Prop({ required: true })
  public productId!: IProduct["id"];

  @Prop({ default: false })
  public isDisabled!: boolean;

  public isLoading = false;
  public isReloading = false;
  public isProcessing = false;
  public requestError = false;

  public dataModule: DataModules = DataModules.CATALOGUE;
  public categoriesModule: DataModules = DataModules.CATALOGUE_CATEGORIES;

  public productTypes: ProductTypes[] = [ProductTypes.SINGLE_PRODUCT];

  public created() {
    if (!this.contentIsAvailable) return;

    this.isLoading = true;
    this.loadData();
  }

  public async loadData() {
    try {
      await Promise.all([
        this.getRootCategories(this.isReloading),
        this.getProductData(this.isReloading)
      ]);
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }
    this.isLoading = false;
    this.isReloading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getRootCategories(ignoredStored = false) {
    return this.$store.dispatch(`data/${this.categoriesModule}/list`, {
      productTypes: this.productTypes,
      scope: this.rootCategoriesScope,
      params: {
        order: "order",
        limit: 0,
        "filter[level]": 1,
        brand_id: this.brandId
      },
      vm: this,
      ignoredStored
    });
  }

  public getProductData(ignoreStored = false) {
    return this.$store.dispatch(`data/${this.dataModule}/get`, {
      id: this.productId,
      scope: this.productWithRelatedScope,
      params: {
        with: "related,related.related_object"
      },
      vm: this,
      ignoreStored: ignoreStored
    });
  }

  public get sortableData() {
    return this.$_.orderBy(
      this.$_.map(this.relatedProducts, relatedProduct => {
        return this.$_.merge({}, relatedProduct, {
          visible: true
        });
      }),
      ["active", "order", "name"],
      ["desc", "asc", "asc"]
    );
  }

  public async editRelatedObject(relatedObject: IRelatedObject) {
    this.$store.dispatch(
      `data/${DataModules.CATALOGUE}/openEditRelatedObjectModal`,
      {
        props: {
          relatedObject
        },
        events: {
          success: () => {
            this.getProductData(true);
          }
        }
      }
    );
  }

  public getName(relatedObject: IRelatedObject) {
    if (this.$_.get(relatedObject, "name"))
      return this.$_.get(relatedObject, "name");

    return this.$_.get(relatedObject, "related_object.name");
  }

  public hasMissingDescription(relatedObject: IRelatedObject) {
    if (relatedObject.use_object_description) return false;
    return !(this.$_.get(relatedObject, "description") || "").length;
  }

  public getDescription(relatedObject: IRelatedObject) {
    if (relatedObject.use_object_description)
      return this.$_.get(relatedObject, "related_object.description") || "--";
    return this.$_.get(relatedObject, "description");
  }

  public isCategory(type) {
    return type === RelatedProductsTypes.CATEGORY;
  }

  public async confirmDetach(relatedObject: IRelatedObject) {
    const modal = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        parent: this,
        props: {
          mode: ConfirmModalModes.BUTTON,
          autoClose: false,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.delete_confirmation", {
            object: (this.$tc("_.recommendations", 1) as string).toLowerCase()
          })
        },
        canCancel: [],
        events: {
          confirmed: async () => {
            try {
              await this.detachCrossSell(relatedObject);
              await this.getProductData(true);

              this.$buefy.toast.open({
                message: this.$t("_.object_successfully_removed", {
                  object: this.$tc("_.recommendations", 1)
                }) as string
              });
              modal.close();
            } catch (error) {
              modal.close();
              this.$store.dispatch("api/handleError", error);
            }
          }
        }
      }
    });
  }

  public async reorder(movedElementsIds: string[]) {
    try {
      this.isProcessing = true;
      await this.$store.dispatch(
        `data/${DataModules.CATALOGUE}/reorderRelatedProducts`,
        {
          id: this.productId,
          data: {
            product_related_ids: movedElementsIds
          }
        }
      );
      await this.getProductData(true);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isProcessing = false;
    }
  }

  public detachCrossSell(relatedObjectId) {
    return this.$store.dispatch(
      `data/${DataModules.CATALOGUE}/detachRelatedProduct`,
      {
        productId: this.productId,
        id: relatedObjectId
      }
    );
  }

  public async openManageProductsModal() {
    if (this.isProcessing) {
      return;
    }

    await this.$store.dispatch(
      `data/${DataModules.CATALOGUE}/openManageCategoriesAndProductsModal`,
      {
        props: {
          productTypes: this.productTypes,
          selectedObjects: this.$_.map(this.sortableData, selectedObject => {
            return {
              id: selectedObject.object_id,
              category_id: this.$_.get(
                selectedObject,
                "related_object.category_id"
              ),
              name: selectedObject.related_object.name,
              object_type: selectedObject.object_type
            };
          }),
          disabledProducts: [this.productId]
        },
        events: {
          success: async results => {
            this.isProcessing = true;

            try {
              await this.attachCrossSells(results);
              await this.detachCrossSells(results);
              await this.getProductData(true);

              this.$store.dispatch("toast/show", {
                message: this.$t("_.object_successfully_updated", {
                  object: this.$tc("_.recommendations", 2)
                })
              });
            } catch (error) {
              this.$store.dispatch("api/handleError", error);
            } finally {
              this.isProcessing = false;
            }
          }
        }
      }
    );
  }

  public get relatedProducts() {
    return this.$_.get(this.product, "related", []);
  }

  public attachCrossSells(selectedCrossSells: ISelectedProduct[]) {
    const newCrossSellsIds = this.$_.differenceWith(
      this.$_.map(selectedCrossSells, (selectedCrossSell: any) => {
        return `${selectedCrossSell.id}_${selectedCrossSell.object_type}`;
      }),
      this.$_.map(this.sortableData, (product: any) => {
        return `${product.object_id}_${product.object_type}`;
      }),
      this.$_.isEqual
    );

    const newCrossSells = this.$_.filter(
      selectedCrossSells,
      (selectedCrossSell: any) => {
        return newCrossSellsIds.includes(
          `${selectedCrossSell.id}_${selectedCrossSell.object_type}`
        );
      }
    );

    if (!newCrossSells.length) return Promise.resolve();

    return this.$store.dispatch(
      `data/${this.dataModule}/attachRelatedProducts`,
      {
        id: this.productId,
        data: {
          related: this.$_.map(newCrossSells, newCrossSell => {
            return {
              object_id: newCrossSell.id,
              object_type: newCrossSell.object_type,
              active: true,
              use_object_description: true
            };
          })
        }
      }
    );
  }

  public detachCrossSells(selectedCrossSells: ISelectedProduct[]) {
    const toDetachIdsWithTypes = this.$_.differenceWith(
      this.$_.map(this.sortableData, (product: any) => {
        return `${product.object_id}_${product.object_type}`;
      }),
      this.$_.map(selectedCrossSells, (selectedCrossSell: any) => {
        return `${selectedCrossSell.id}_${selectedCrossSell.object_type}`;
      }),
      this.$_.isEqual
    );

    const toDetachCrossSellsIds = this.$_.map(
      this.$_.filter(this.sortableData, (product: any) => {
        return toDetachIdsWithTypes.includes(
          `${product.object_id}_${product.object_type}`
        );
      }),
      toDetachCrossSell => toDetachCrossSell.id
    );

    if (!toDetachCrossSellsIds.length) return Promise.resolve();

    return Promise.all(
      this.$_.map(toDetachCrossSellsIds, toDetachCrossSellId => {
        return this.detachCrossSell(toDetachCrossSellId);
      })
    );
  }

  public get guardFunctionalities() {
    return ["staff_get_product", "list_products", "list_product_categories"];
  }

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: this.categoriesModule,
      scope: this.rootCategoriesScope,
      vm: this
    });

    this.$store.dispatch("data/bin", {
      storeModule: this.dataModule,
      scope: this.productWithRelatedScope,
      vm: this
    });
  }
}
